import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from '../../components/SnackbarProvider';
import axiosInstance from '../../lib/axios';
import { palette } from '../../theme/colors';
import type { CashDrawerShift, CloseShiftPayload } from '../../types/cashDrawer';

interface CloseShiftModalProps {
  open: boolean;
  onClose: () => void;
  shift: CashDrawerShift | null;
}

interface FormData {
  countedClosingBalance: number;
  notes: string;
}

const defaultValues: FormData = {
  countedClosingBalance: 0,
  notes: '',
};

const formatCurrency = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const CloseShiftModal = ({ open, onClose, shift }: CloseShiftModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const [closedShift, setClosedShift] = useState<CashDrawerShift | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ defaultValues });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setClosedShift(null);
    }
  }, [open, reset]);

  const closeShiftMutation = useMutation({
    mutationFn: async (data: CloseShiftPayload): Promise<CashDrawerShift> => {
      const response = await axiosInstance.post(`/cash-drawer/shifts/${shift?.id}/close`, data);
      return response.data;
    },
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: ['cash-drawer-shifts'] });
      queryClient.invalidateQueries({ queryKey: ['cash-drawer-current-shift'] });
      setClosedShift(result);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('cashDrawer.closeShiftModal.error');
      showMessage(message, 'error');
    },
  });

  const onSubmit = async (data: FormData) => {
    await closeShiftMutation.mutateAsync({
      countedClosingBalance: Number(data.countedClosingBalance),
      notes: data.notes || undefined,
    });
  };

  const handleDone = () => {
    onClose();
  };

  // Once closedShift is set (the close mutation succeeded), keep showing the result view even if
  // the parent's `shift` prop later becomes null — closing a shift invalidates the
  // cash-drawer-current-shift query, which POS's `currentShift` (passed through as `shift`) then
  // refetches to null once the shift is no longer open. Without this, the discrepancy result view
  // could vanish out from under the user the instant that refetch resolves, before they've had a
  // chance to read it.
  if (!shift && !closedShift) {
    return null;
  }

  const discrepancy = closedShift?.discrepancy;
  const discrepancyColor =
    discrepancy === null || discrepancy === undefined || discrepancy === 0
      ? palette.textPrimary
      : discrepancy > 0
        ? palette.success
        : palette.error;

  return (
    <Dialog open={open} onClose={closedShift ? handleDone : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('cashDrawer.closeShiftModal.title')}</Typography>
          <IconButton onClick={closedShift ? handleDone : onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {closedShift ? (
        <>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">
                  {t('cashDrawer.expectedClosingBalance')}
                </Typography>
                <Typography fontWeight={600}>
                  {formatCurrency(closedShift.expectedClosingBalance)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography color="text.secondary">
                  {t('cashDrawer.countedClosingBalance')}
                </Typography>
                <Typography fontWeight={600}>
                  {formatCurrency(closedShift.countedClosingBalance)}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                pt={1}
                borderTop={1}
                borderColor="divider"
              >
                <Typography color="text.secondary">{t('cashDrawer.discrepancy')}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: discrepancyColor }}>
                  {discrepancy !== null && discrepancy !== undefined && discrepancy > 0 ? '+' : ''}
                  {formatCurrency(discrepancy)}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button variant="contained" onClick={handleDone}>
              {t('cashDrawer.closeShiftModal.done')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {/* shift is guaranteed non-null here: the only way to reach this (non-result)
                      branch is closedShift being falsy, and the guard above already returned null
                      when both shift and closedShift are falsy. */}
                  {t('cashDrawer.openingBalance')}: {formatCurrency(shift?.openingBalance ?? null)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="countedClosingBalance"
                  control={control}
                  rules={{ required: true, min: 0 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label={t('cashDrawer.countedClosingBalance')}
                      fullWidth
                      required
                      inputProps={{ min: 0, step: '0.01' }}
                      error={!!errors.countedClosingBalance}
                      helperText={
                        errors.countedClosingBalance
                          ? t('cashDrawer.closeShiftModal.balanceRequired')
                          : ''
                      }
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('cashDrawer.closeShiftModal.notes')}
                      fullWidth
                      multiline
                      rows={2}
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} disabled={isSubmitting}>
              {t('cashDrawer.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
            >
              {t('cashDrawer.closeShiftModal.submit')}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default CloseShiftModal;
